import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

const MAPBOX_SCRIPT_ID = 'mapbox_GL_JS';
const GOOGLE_MAPS_SCRIPT_ID = 'GoogleMapsApi';

/**
 * Include scripts (like Map Provider).
 * These scripts are relevant for whole application: location search in Topbar and maps on different pages.
 * However, if you don't need location search and maps, you can just omit this component from app.js
 * Note: another common point to add <scripts>, <links> and <meta> tags is Page.js
 *       and Stripe script is added in public/index.html
 *
 * Note 2: When adding new external scripts/styles/fonts/etc.,
 *         if a Content Security Policy (CSP) is turned on, the new URLs
 *         should be whitelisted in the policy. Check: server/csp.js
 */
export const IncludeScripts = props => {
  const { marketplaceRootURL: rootURL, maps, analytics } = props?.config || {};
  const googleAnalyticsId = analytics.googleAnalyticsId;
  const openaiAdsPixelId =
    analytics?.openaiAdsPixelId || process.env.REACT_APP_OPENAI_ADS_PIXEL_ID || '';

  const { mapProvider, googleMapsAPIKey, mapboxAccessToken } = maps || {};
  const isGoogleMapsInUse = mapProvider === 'googleMaps';
  const isMapboxInUse = mapProvider === 'mapbox';

  // The Mapbox stylesheet styles the (below-the-fold) map. Shipping it as a normal
  // stylesheet made it render-blocking in <head> on SSR loads (~1s on the critical path).
  // We render it with media="print" (downloaded, but non-render-blocking) and promote it to
  // media="all" once mounted on the client. This avoids inline onload handlers (CSP-safe).
  const [mapboxCssMedia, setMapboxCssMedia] = useState('print');
  useEffect(() => {
    setMapboxCssMedia('all');
  }, []);

  // Add Google Analytics script if correct id exists (it should start with 'G-' prefix)
  // See: https://developers.google.com/analytics/devguides/collection/gtagjs
  const hasGoogleAnalyticsv4Id = googleAnalyticsId?.indexOf('G-') === 0;

  // Collect relevant map libraries
  let mapLibraries = [];
  let analyticsLibraries = [];

  if (isMapboxInUse) {
    // NOTE: remember to update mapbox-sdk.min.js to a new version regularly.
    // mapbox-sdk.min.js is included from static folder for CSP purposes.
    // `defer`: this is the geocoding SDK used by Topbar location search, which reads it
    // lazily (GeocoderMapbox.getClient is only called when a user searches). It was
    // render-blocking in <head>; deferring keeps it off the critical path while still
    // executing before DOMContentLoaded, like mapbox-gl.js below.
    mapLibraries.push(
      <script
        key="mapboxSDK"
        src={`${rootURL}/static/scripts/mapbox/mapbox-sdk.min.js`}
        defer
      ></script>
    );
    // Add CSS for Mapbox map (non-render-blocking via media swap — see mapboxCssMedia above)
    mapLibraries.push(
      <link
        key="mapbox_GL_CSS"
        href="https://api.mapbox.com/mapbox-gl-js/v1.0.0/mapbox-gl.css"
        rel="stylesheet"
        media={mapboxCssMedia}
        crossOrigin
      />
    );
    // Add Mapbox library.
    // `defer`: mapbox-gl.js is ~170KB and was render-blocking in <head> on SSR loads,
    // delaying first paint on every page (incl. the map-less homepage). Deferring keeps
    // it off the critical path; it still executes before DOMContentLoaded so the Topbar
    // location search (window.mapboxgl.LngLat) is ready by the time a user interacts.
    // Map components recover via a readiness check in Map.js if they mount first.
    mapLibraries.push(
      <script
        id={MAPBOX_SCRIPT_ID}
        key="mapbox_GL_JS"
        src="https://api.mapbox.com/mapbox-gl-js/v1.0.0/mapbox-gl.js"
        defer
        crossOrigin
      ></script>
    );
  } else if (isGoogleMapsInUse) {
    // Add Google Maps library
    mapLibraries.push(
      <script
        id={GOOGLE_MAPS_SCRIPT_ID}
        key="GoogleMapsApi"
        src={`https://maps.googleapis.com/maps/api/js?key=${googleMapsAPIKey}&libraries=places`}
        crossOrigin
      ></script>
    );
  }

  if (googleAnalyticsId && hasGoogleAnalyticsv4Id) {
    // Google Analytics: gtag.js
    // NOTE: This template is a single-page application (SPA).
    //       gtag.js sends initial page_view event after page load.
    //       but we need to handle subsequent events for in-app navigation.
    //       This is done in src/analytics/handlers.js
    analyticsLibraries.push(
      <script
        key="gtag.js"
        async
        src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`}
        crossOrigin
      ></script>
    );

    analyticsLibraries.push(
      <script key="gtag dataLayer">
        {`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        window.__PROMOTIX_GA4_MEASUREMENT_ID__ = '${googleAnalyticsId}';
      
        gtag('config', '${googleAnalyticsId}', {
          cookie_flags: 'SameSite=None;Secure',
        });
        `}
      </script>
    );
  }

  // OpenAI Ads (ChatGPT) Measurement Pixel
  // https://developers.openai.com/ads/measurement-pixel
  // SPA page_viewed + funnel events: src/util/openaiAds.js + src/analytics/handlers.js
  if (openaiAdsPixelId) {
    analyticsLibraries.push(
      <script key="openai-ads-oaiq">
        {`
        window.oaiq = window.oaiq || function () {
          (window.oaiq.q = window.oaiq.q || []).push(arguments);
        };
        oaiq("init", { pixelId: "${openaiAdsPixelId}" });
        `}
      </script>
    );
    analyticsLibraries.push(
      <script
        key="openai-ads-sdk"
        async
        src="https://bzrcdn.openai.com/sdk/oaiq.min.js"
      ></script>
    );
  }

  const isBrowser = typeof window !== 'undefined';
  const isMapboxLoaded = isBrowser && window.mapboxgl;

  // If Mapbox is loaded, we can set the accessToken already here.
  // This is the execution flow with the production build,
  // since SSR includes those map libraries to <head> of the app.
  if (isMapboxInUse && isMapboxLoaded && !window.mapboxgl.accessToken) {
    // Add access token for Mapbox library
    window.mapboxgl.accessToken = mapboxAccessToken;
  }

  // If the script is added on client side as a reaction to page navigation or
  // the app is rendered on client side entirely (e.g. HMR/WebpackDevServer),
  // we need to listen when the script is loaded.
  const onMapLibLoaded = () => {
    // At this point we know that map library is loaded after it's dynamically included
    if (isMapboxInUse && !window.mapboxgl.accessToken) {
      // Add access token for Mapbox sdk.
      window.mapboxgl.accessToken = mapboxAccessToken;
    }
  };

  // React Helmet Async doesn't support onLoad prop for scripts.
  // However, it does have onChangeClientState functionality.
  // We can use that to start listen 'load' events when the library is added on client-side.
  const onChangeClientState = (newState, addedTags) => {
    if (addedTags && addedTags.scriptTags) {
      const foundScript = addedTags.scriptTags.find(s =>
        [MAPBOX_SCRIPT_ID, GOOGLE_MAPS_SCRIPT_ID].includes(s.id)
      );
      if (foundScript) {
        foundScript.addEventListener('load', onMapLibLoaded, { once: true });
      }
    }
  };

  const allScripts = [...analyticsLibraries, ...mapLibraries];
  return <Helmet onChangeClientState={onChangeClientState}>{allScripts}</Helmet>;
};
